import { Avatar, AvatarGroup, Text, Box, Flex } from '@chakra-ui/react';
import { useVendor } from '@outdoorly/react-chat-sdk';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import { EXPERT_IMAGES } from '../../constants';
import { Store } from '../../store';
import { setIntroField } from '../../utils';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { IntroChatButton } from './IntroChatButton';
import posthog from 'posthog-js';
import { OnlineIndicator } from './OnlineIndicator';
import { Vetted } from '../../icons';

type IntroductionMessageProps = {
  setExpertHelpActivated?: (arg0: boolean) => void;
  showActivateChatButton?: boolean;
};

export const IntroductionMessage: React.FC<IntroductionMessageProps> = (props) => {
  const { setExpertHelpActivated, showActivateChatButton } = props;
  const { vendor } = useVendor();
  const featuredExpert = vendor?.featuredProfile;
  const labels = Store.useState((s) => s.labels).substitute({
    expertName: featuredExpert?.publicName
  });
  const showSingleExpert = Store.useState(
    (s) => s.featureFlags['expert-activator-type'] === 'single-expert-activator'
  );
  const expertImages = useMemo(() => {
    return [...new Array(4)].map((_, index) => {
      return tryToBuildImgixUrl(vendor?.expertAvatarLineup?.[index] ?? EXPERT_IMAGES[index], {
        w: 50,
        h: 50,
        auto: 'format,compress',
        crop: 'faces',
        fit: 'crop'
      });
    });
  }, [vendor]);
  return (
    <>
      <Flex align="center" justify="space-between" mt="4px">
        {showSingleExpert && featuredExpert?.avatar ? (
          <Flex align="center">
            <Avatar
              src={tryToBuildImgixUrl(featuredExpert?.avatar, {
                w: 50,
                h: 50,
                auto: 'format,compress',
                crop: 'faces',
                fit: 'crop'
              })}
              w="36px"
              h="36px"
              border={0}
              ignoreFallback
              role="presentation"
              aria-hidden="true"
            />
            <Text variant="label-large" mx="4px" maxW="114px">
              {featuredExpert.publicName}
            </Text>
            <OnlineIndicator />
          </Flex>
        ) : (
          <Box
            boxShadow="0px 2px 4px rgba(0, 0, 0, 0.06)"
            background={showActivateChatButton ? 'scheme.backgroundPrimary' : 'scheme.backgroundSecondary'}
            borderRadius="50px"
            width="fit-content"
            padding="4px"
          >
            <AvatarGroup size="md">
              {expertImages.map((expert, index) => (
                <Avatar
                  key={index}
                  src={expert}
                  w="36px"
                  h="36px"
                  border={0}
                  ignoreFallback
                  role="presentation"
                  aria-hidden="true"
                />
              ))}
            </AvatarGroup>
          </Box>
        )}

        <Flex align="center">
          <Vetted fill="scheme.purple4" w="11px" mr="7px" ml="17px" />
          <Text color="scheme.contentSecondary" variant="label-small" letterSpacing="-0.1px" mr="9px">
            {showSingleExpert ? labels.vettedExpert : labels.vettedExperts}
          </Text>
        </Flex>
      </Flex>

      <Box mt="12px" mb={showActivateChatButton ? '12px' : 0}>
        <Text variant="paragraph-medium" color="scheme.contentSecondary">
          <span style={{ fontWeight: 700 }}>
            {showSingleExpert ? labels.needHelpPickingSingleExpert : labels.needHelpPicking}
          </span>
          {` ${
            showSingleExpert ? labels.chatWithExpertActivatorSingleExpert : labels.chatWithExpertActivator
          }`}
        </Text>
      </Box>
      {showActivateChatButton && (
        <IntroChatButton
          onClick={() => {
            posthog.capture('CLICK-CHAT-WITH-EXPERT');
            setIntroField(`introMessage-fa3fc71d-2ba4-46f6-a354-d42a4a04443e`, labels.chatWithExpert);
            setIntroField(`messageOptionTime-fa3fc71d-2ba4-46f6-a354-d42a4a04443e`, dayjs().toString());
            setExpertHelpActivated?.(true);
          }}
          labels={labels}
        />
      )}
    </>
  );
};
